'use client';

/**
 * Crawler context — exposes the server-detected `isCrawler` boolean to any
 * client component that needs to render differently for bots vs humans.
 *
 * Why: navigator.userAgent does NOT identify Google's WRS (Web Rendering
 * Service) as a crawler — it reports a regular Chrome UA in JS even though
 * the HTTP request header is "Googlebot". So we detect server-side from the
 * request header (lib/is-crawler.ts) and propagate via this context.
 *
 * Used by:
 * - MotionReveal: renders plain div (no Framer Motion) for bots, so content
 *   isn't trapped at opacity:0 waiting for whileInView to trigger.
 * - VideoBackground: skips entirely for bots.
 * - Anywhere else that wants to ship a lighter / more screenshot-friendly
 *   variant to crawlers.
 */

import { createContext, useContext, type ReactNode } from 'react';

const CrawlerContext = createContext<boolean>(false);

export function CrawlerProvider({
  isCrawler,
  children,
}: {
  isCrawler: boolean;
  children: ReactNode;
}) {
  return <CrawlerContext.Provider value={isCrawler}>{children}</CrawlerContext.Provider>;
}

export function useIsCrawler(): boolean {
  return useContext(CrawlerContext);
}
